import React from "react";
import PropTypes from "prop-types";
import { Box, Container, useTheme } from "@mui/material";
import {useParams} from "react-router-dom";

//component imports
import NavBar from "../components/navBar/index.jsx";
import { SideBar } from "../components/sideBar/index.jsx";

import withStyles from '@mui/styles/withStyles';
import { COLORS } from "../constants/colors.js";

//Localization imports
import { withTranslation } from "react-i18next";

//API imports
import { fetchVessel } from "../api/vessel.js";

// Logger
import { logger } from "../api/logger";
import { NAVIGATION_BAR_HEIGHT } from "../constants/layout.js";

const paper = {
  backgroundColor: `${COLORS.baseColor} !important`,
};

const paperTitle = (color) => ({
  "& p": {
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: "500",
    fontStretch: "normal",
    lineHeight: "normal",
    letterSpacing: "normal",
    color,
    margin: "0",
  },
  width: "max",
  height: "47px",
  backgroundColor: "rgba(44, 58, 72, 0.4)",
  margin: "-16px -16px 0px -16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px 12px 0px 0px"
});

const styles = (theme) => ({
  paper: {
    ...paper,
    padding: theme.spacing(2),
    borderRadius: "12px",
  },
  controlPaper: {
    ...paper,
    padding: "16px 16px 0",
    borderRadius: "12px",
  },
  root: {
    flexGrow: 1,
    marginTop: "16px",
    [theme.breakpoints.up('xl')]: {
      marginRight: "50px",
      marginLeft: "50px",
    },
    [theme.breakpoints.down('xl')]: {
      marginRight: "32px",
      marginLeft: "32px"
    },
    marginBottom: "16px",
  },
  paperTitle: {
    ...paperTitle("#19b3ab"),
  },
  disconnectedPaperTitle: {
    ...paperTitle("#c8c8c8"),
  },
  panelNavBar: {
    height: "calc(100dvh - 140px)",
    overflowY: "auto",
    overflowX: "hidden",
  },
  base: {
    display: "flex",
  },
  sideBar: {
    top: NAVIGATION_BAR_HEIGHT + "px",
    marginTop: NAVIGATION_BAR_HEIGHT + "px",
  },
  container: {
    padding: "0 !important",
  },
  wideContainer: {
    padding: "0 !important",
    overflow: "hidden",
  },
});

const PanelLayout = (props) => {
  const { t } = props;
  const theme = useTheme();
  const { vesselId } = useParams(); //Get vesselId from router;
  const [vesselName, setVesselName] = React.useState("");
  const [vesselType, setVesselType] = React.useState("");
  const unmount = React.useRef(false);
  const currentPath = location.pathname.split("/")[1];

  const fetchVesselInfo = async () => {
    try {
      if (!vesselId) {
        return;
      }

      const data = await fetchVessel(vesselId);

      if (data.vessel) {
        setVesselName(data.vessel.name);
        setVesselType(data.vessel.firstClass);
      }
    } catch (e) {
      logger.error(`fetchVesselInfo error: ${e}`);
    }
  };

  React.useEffect(() => {
    unmount.current || fetchVesselInfo();
    return () => {
      unmount.current = true;
    };
  }, []);

  return (
    <div className={props.classes.base}>
      <NavBar
        title={t(props.name)}
        vesselId={vesselId}
        miniIcons={true}
        changeLanguage={props.defaultParams.changeLanguage}
        setUserContext={props.defaultParams.setUserContext}
        vesselName={vesselName}
        vesselType={vesselType}
        includeVesselSelect={currentPath !== "servicepolicy" && currentPath !== "releasenotes"}
      />
      <SideBar className={props.classes.sideBar} />
      <Container
        maxWidth={false}
        className={
          ["trend", "cargo"].includes(currentPath)
            ? props.classes.wideContainer
            : props.classes.container
        }
      >
        <Box sx={{
          ...props.classes.panelNavBar,
          marginTop: "140px",
          [theme.breakpoints.down('sm')]: {
            marginTop: "70px",
          }
        }}>
          <props.component
            vesselName={vesselName}
            layoutStyle={props.classes}
            {...props.defaultParams}
          />
        </Box>
      </Container>
    </div>
  );
};

PanelLayout.propTypes = {
  name: PropTypes.string,
  t: PropTypes.func,
  classes: PropTypes.objectOf(PropTypes.string.isRequired).isRequired,
  component: PropTypes.elementType.isRequired,
  defaultParams: PropTypes.shape({
    changeLanguage: PropTypes.func.isRequired,
    setUserContext: PropTypes.func.isRequired,
  }).isRequired,
};

export default withStyles(styles)(withTranslation()(PanelLayout));
